/**
 * Bulk Upload Tab Component
 * Displays bulk upload options for students and clinical sites
 */
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { Button } from "../ui/button";
import { GraduationCap, Hospital, Download, Upload, FileSpreadsheet } from "lucide-react";

export function BulkUploadTab({ onDownloadTemplate, onUpload }) {
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-2 gap-6">
        {/* Students Upload Card */}
        <Card className="bg-[#151A23] border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-cyan-400" />
              </div>
              <div>
                <CardTitle className="text-white">Students</CardTitle>
                <CardDescription className="text-slate-400">
                  Bulk import student records
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-400">
              Upload a CSV file with student information. Required columns: First Name, Last Name, email, program.
              Optional: preferred_specialty, address, city, state, zip_code.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onDownloadTemplate('students')}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="download-students-template"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              <Button
                onClick={() => onUpload('students')}
                className="flex-1 bg-cyan-400 text-black hover:bg-cyan-300"
                data-testid="upload-students-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Clinical Sites Upload Card */}
        <Card className="bg-[#151A23] border-slate-700">
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <Hospital className="w-6 h-6 text-violet-400" />
              </div>
              <div>
                <CardTitle className="text-white">Clinical Sites</CardTitle>
                <CardDescription className="text-slate-400">
                  Bulk import clinical site locations
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-400">
              Upload a CSV file with clinical site information. Required columns: name, address, city, state, zip_code, specialty, contact_email, contact_phone.
              Optional: description.
            </p>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => onDownloadTemplate('clinical-sites')}
                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
                data-testid="download-clinical-sites-template"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
              <Button
                onClick={() => onUpload('clinical-sites')}
                className="flex-1 bg-violet-500 text-white hover:bg-violet-400"
                data-testid="upload-clinical-sites-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload CSV
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Instructions */}
      <Card className="bg-[#151A23] border-slate-700">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-400" />
            CSV Upload Instructions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h4 className="text-slate-200 font-medium mb-2">How to prepare your CSV:</h4>
              <ol className="text-sm text-slate-400 space-y-2 list-decimal list-inside">
                <li>Download the template for the type of data you want to import</li>
                <li>Fill in your data following the column headers</li>
                <li>Save the file as a CSV (comma-separated values)</li>
                <li>Click &quot;Upload CSV&quot; and select your file</li>
              </ol>
            </div>
            <div>
              <h4 className="text-slate-200 font-medium mb-2">Tips:</h4>
              <ul className="text-sm text-slate-400 space-y-2 list-disc list-inside">
                <li>The first row must contain column headers</li>
                <li>Invalid rows will be skipped and reported</li>
                <li>Duplicate emails will be flagged as errors</li>
                <li>Clinical sites will be automatically geocoded</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
